import React from "react";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardHeader,
  CardBody,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Alert,
  AlertIcon,
  Spinner,
  Heading,
  Divider,
  SimpleGrid,
  Badge,
  Button,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useInstitutionConfig } from "./InstitutionConfigContext";

export default function SecurityTab() {
  const {
    config,
    decodedInstitution,
    session,
    watermarkFormData,
    setWatermarkFormData,
    watermarkConfig,
    fetchingWatermark,
    watermarkFetchError,
    creatingWatermark,
    updatingWatermark,
    handleWatermarkConfigSave,
    resetWatermarkForm,
  } = useInstitutionConfig();

  return (
    <VStack spacing={8} align="stretch">
      {/* Watermark & Copyright Protection */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">Watermark & Copyright Protection</Heading>
          <Text fontSize="sm" color="gray.400" mt={2}>
            Configure watermarks and copyright protection for scenarios and documents
          </Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* Master Toggle */}
            <FormControl>
              <HStack justify="space-between" w="full">
                <VStack align="start" spacing={0} flex="1">
                  <HStack>
                    <FormLabel color="white" mb={0}>Enable Watermarking</FormLabel>
                    <Badge colorScheme={watermarkFormData.watermarkingEnabled ? "green" : "gray"} fontSize="xs">
                      {watermarkFormData.watermarkingEnabled ? "Active" : "Disabled"}
                    </Badge>
                  </HStack>
                  <Text fontSize="sm" color="gray.400">
                    All scenarios and documents will include visible and hidden protection when enabled
                  </Text>
                </VStack>
                <Switch
                  isChecked={watermarkFormData.watermarkingEnabled}
                  onChange={(e) => setWatermarkFormData(prev => ({ ...prev, watermarkingEnabled: e.target.checked }))}
                  colorScheme="blue"
                  size="lg"
                />
              </HStack>
            </FormControl>

            {/* Live Preview */}
            {watermarkFormData.watermarkingEnabled && (
              <Box
                p={6}
                bg="gray.900"
                borderRadius="md"
                border="2px dashed"
                borderColor="blue.500"
                position="relative"
                minH="150px"
              >
                <Text fontSize="xs" color="blue.400" mb={2} fontWeight="bold">LIVE PREVIEW</Text>
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform={`translate(-50%, -50%) rotate(-45deg)`}
                  fontSize="4xl"
                  fontWeight="bold"
                  color={watermarkFormData.overtWatermarkColor}
                  opacity={watermarkFormData.overtWatermarkOpacity}
                  whiteSpace="nowrap"
                  userSelect="none"
                  pointerEvents="none"
                >
                  {watermarkFormData.overtWatermarkText
                    .replace('{year}', new Date().getFullYear())
                    .replace('{institution}', decodedInstitution)
                    .replace('{user}', session?.user?.email || 'user@example.com')
                  }
                </Box>
                <Text color="gray.400" fontSize="sm" position="relative" zIndex={1}>
                  Sample scenario content would appear here. The watermark shows how it will look on actual documents.
                </Text>
              </Box>
            )}

            {watermarkFormData.watermarkingEnabled && (
              <>
                <Divider borderColor="gray.600" />

                {/* Overt Watermark Settings */}
                <Box>
                  <Heading size="sm" color="white" mb={4}>Visible Watermark Settings</Heading>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel color="white">Watermark text template</FormLabel>
                      <Input
                        value={watermarkFormData.overtWatermarkText}
                        onChange={(e) => setWatermarkFormData(prev => ({ ...prev, overtWatermarkText: e.target.value }))}
                        placeholder={"\u00a9 {year} {institution} - Educational Use Only"}
                        bg="gray.700"
                        borderColor="gray.600"
                        color="white"
                      />
                      <Text fontSize="xs" color="gray.400" mt={1}>
                        Placeholders: {"{year}"}, {"{institution}"}, {"{user}"}
                      </Text>
                    </FormControl>

                    <FormControl>
                      <FormLabel color="white" fontSize="sm">Quick Presets</FormLabel>
                      <Wrap spacing={2}>
                        <WrapItem>
                          <Button size="xs" variant="outline" colorScheme="blue"
                            onClick={() => setWatermarkFormData(prev => ({
                              ...prev, overtWatermarkText: "\u00a9 {year} {institution} - Educational Use Only"
                            }))}>
                            Standard
                          </Button>
                        </WrapItem>
                        <WrapItem>
                          <Button size="xs" variant="outline" colorScheme="purple"
                            onClick={() => setWatermarkFormData(prev => ({
                              ...prev, overtWatermarkText: "CONFIDENTIAL - {institution} Internal Training"
                            }))}>
                            Confidential
                          </Button>
                        </WrapItem>
                        <WrapItem>
                          <Button size="xs" variant="outline" colorScheme="orange"
                            onClick={() => setWatermarkFormData(prev => ({
                              ...prev, overtWatermarkText: "DRAFT - Not for Distribution"
                            }))}>
                            Draft
                          </Button>
                        </WrapItem>
                        <WrapItem>
                          <Button size="xs" variant="outline" colorScheme="red"
                            onClick={() => setWatermarkFormData(prev => ({
                              ...prev, overtWatermarkText: "PROPRIETARY - {institution} \u00a9 {year}"
                            }))}>
                            Proprietary
                          </Button>
                        </WrapItem>
                      </Wrap>
                    </FormControl>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <FormControl>
                        <FormLabel color="white">Watermark position</FormLabel>
                        <Select
                          value={watermarkFormData.overtWatermarkPosition}
                          onChange={(e) => setWatermarkFormData(prev => ({ ...prev, overtWatermarkPosition: e.target.value }))}
                          bg="gray.700" borderColor="gray.600" color="white">
                          <option value="diagonal">Diagonal</option>
                          <option value="top">Top</option>
                          <option value="bottom">Bottom</option>
                          <option value="corners">Corners</option>
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel color="white">Watermark color</FormLabel>
                        <HStack>
                          <Input type="color" value={watermarkFormData.overtWatermarkColor}
                            onChange={(e) => setWatermarkFormData(prev => ({ ...prev, overtWatermarkColor: e.target.value }))}
                            bg="gray.700" borderColor="gray.600" w="60px" />
                          <Input value={watermarkFormData.overtWatermarkColor}
                            onChange={(e) => setWatermarkFormData(prev => ({ ...prev, overtWatermarkColor: e.target.value }))}
                            placeholder="#FF0000" bg="gray.700" borderColor="gray.600" color="white" />
                        </HStack>
                      </FormControl>

                      <FormControl>
                        <FormLabel color="white">Watermark opacity ({Math.round(watermarkFormData.overtWatermarkOpacity * 100)}%)</FormLabel>
                        <Input type="range" min="0" max="1" step="0.01"
                          value={watermarkFormData.overtWatermarkOpacity}
                          onChange={(e) => setWatermarkFormData(prev => ({ ...prev, overtWatermarkOpacity: parseFloat(e.target.value) }))}
                          bg="gray.700" borderColor="gray.600" />
                      </FormControl>
                    </SimpleGrid>
                  </VStack>
                </Box>

                <Divider borderColor="gray.600" />

                {/* Subtle Watermark Settings */}
                <Box>
                  <Heading size="sm" color="white" mb={4}>Hidden Watermark Settings</Heading>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <HStack justify="space-between">
                        <VStack align="start" spacing={0}>
                          <FormLabel color="white" mb={0}>Include hidden tracking data</FormLabel>
                          <Text fontSize="sm" color="gray.400">
                            Hidden watermarks allow AI detection and forensic tracking of leaked materials
                          </Text>
                        </VStack>
                        <Switch
                          isChecked={watermarkFormData.subtleWatermarkEnabled}
                          onChange={(e) => setWatermarkFormData(prev => ({ ...prev, subtleWatermarkEnabled: e.target.checked }))}
                          colorScheme="purple"
                        />
                      </HStack>
                    </FormControl>

                    {watermarkFormData.subtleWatermarkEnabled && (
                      <FormControl>
                        <FormLabel color="white">Pattern UUID (auto-generated)</FormLabel>
                        <Input
                          value={watermarkFormData.subtleWatermarkPattern || watermarkConfig?.subtleWatermarkPattern || "Will be generated on save"}
                          isReadOnly bg="gray.700" borderColor="gray.600" color="gray.300" />
                      </FormControl>
                    )}
                  </VStack>
                </Box>

                <Divider borderColor="gray.600" />

                {/* PDF Protection Settings */}
                <Box>
                  <Heading size="sm" color="white" mb={4}>PDF Protection Settings</Heading>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <HStack justify="space-between">
                        <VStack align="start" spacing={0}>
                          <FormLabel color="white" mb={0}>Apply PDF restrictions</FormLabel>
                          <Text fontSize="sm" color="gray.400">
                            Warning text displayed in downloaded PDFs. Restrictions prevent copying and editing.
                          </Text>
                        </VStack>
                        <Switch
                          isChecked={watermarkFormData.pdfProtectionEnabled}
                          onChange={(e) => setWatermarkFormData(prev => ({ ...prev, pdfProtectionEnabled: e.target.checked }))}
                          colorScheme="orange"
                        />
                      </HStack>
                    </FormControl>

                    {watermarkFormData.pdfProtectionEnabled && (
                      <FormControl>
                        <FormLabel color="white">PDF warning text</FormLabel>
                        <Textarea
                          value={watermarkFormData.pdfWarningText?.markdown || ""}
                          onChange={(e) => setWatermarkFormData(prev => ({
                            ...prev,
                            pdfWarningText: { markdown: e.target.value }
                          }))}
                          placeholder="This document is for educational purposes only. Unauthorized distribution is prohibited."
                          bg="gray.700" borderColor="gray.600" color="white" rows={4} />
                      </FormControl>
                    )}
                  </VStack>
                </Box>
              </>
            )}

            {/* Action Buttons */}
            <HStack spacing={4} pt={4}>
              <Button onClick={handleWatermarkConfigSave} colorScheme="blue"
                isLoading={creatingWatermark || updatingWatermark} loadingText="Saving...">
                Save Watermark Settings
              </Button>
              <Button variant="outline" onClick={resetWatermarkForm}>
                Reset to Saved
              </Button>
            </HStack>

            {/* Status Messages */}
            {fetchingWatermark && (
              <HStack spacing={2}>
                <Spinner size="sm" color="blue.400" />
                <Text fontSize="sm" color="gray.300">Loading watermark configuration...</Text>
              </HStack>
            )}

            {watermarkFetchError && (
              <Alert status="info">
                <AlertIcon />
                <Text fontSize="sm">No watermark configuration found. Default settings will be used.</Text>
              </Alert>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* System Information */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">System Information</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" color="gray.400">Created At</Text>
              <Text fontSize="md" color="white">
                {config?.createdAt ? new Date(config.createdAt).toLocaleDateString() : "N/A"}
              </Text>
            </VStack>
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" color="gray.400">Updated At</Text>
              <Text fontSize="md" color="white">
                {config?.updatedAt ? new Date(config.updatedAt).toLocaleDateString() : "N/A"}
              </Text>
            </VStack>
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" color="gray.400">Configured By</Text>
              <Text fontSize="md" color="white">
                {config?.configuredBy?.email || "System"}
              </Text>
            </VStack>
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" color="gray.400">Last Modified By</Text>
              <Text fontSize="md" color="white">
                {config?.lastModifiedBy?.email || "N/A"}
              </Text>
            </VStack>
          </SimpleGrid>
        </CardBody>
      </Card>
    </VStack>
  );
}
